import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Swatch } from '@televet/kibble-ui/build/components/Swatch';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { ConversationCard } from '@televet/kibble-ui/build/patterns/ConversationCard';
import { getChannelParticipants } from 'pages/Conversations/utils';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { replaceParam, RouteDefinitions } from 'routes';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { TeletriageConversationFragment, ChannelViewChannelMemberFragment } from 'shared/types/graphql';
import { getClinicPetParentDisplayName } from 'shared/utils';
import CardMessage from '../../ChannelList/ChannelListItem/CardMessage';
import ChannelAvatar from '../../ChannelList/ChannelListItem/ChannelAvatar';
import { usePetParentsWithDisplayName } from 'shared/hooks/usePetParentsWithDisplayName';

interface TeletriageConversationCardProps {
  conversation: TeletriageConversationFragment;
  hasUnreadMessages: boolean;
  sortByLastMessageEnabled?: boolean;
  isLoadingNextPage?: boolean;
}

export const TeletriageConversationCard = ({
  conversation,
  hasUnreadMessages,
  sortByLastMessageEnabled,
  isLoadingNextPage,
}: TeletriageConversationCardProps): JSX.Element => {
  const { channelId } = useParams<{ channelId: string }>();
  const history = useHistory();
  const { setCurrentClinicPetParentId, viewClinicPetParent } = useSidePanelSearch();
  const { currentClinicId } = useClinicUser();
  const { channelPetParents } = getChannelParticipants(
    conversation.channelMembers as ChannelViewChannelMemberFragment[],
  );
  const firstPetParentId = useMemo(() => channelPetParents[0]?.id, [channelPetParents]);
  const conversationClinicId = useMemo(() => conversation.clinic?.id || '', [conversation.clinic?.id]);
  const isConversationSelected = useMemo(
    () => !!channelId && conversation.id === channelId,
    [channelId, conversation.id],
  );
  const isCurrentClinic = useMemo(
    () => conversationClinicId === currentClinicId,
    [conversationClinicId, currentClinicId],
  );

  // Header
  const { petParentsWithDisplayName } = usePetParentsWithDisplayName({
    petParents: channelPetParents,
  });

  const handleAvatarClick = useCallback(async () => {
    if (!isCurrentClinic) {
      history.push(`${replaceParam(RouteDefinitions.Conversations, ':channelId', conversation.id)}`);
    }
  }, [conversation.id, history, isCurrentClinic]);

  const channelAvatars = useMemo(() => {
    return petParentsWithDisplayName.map(({ id, displayName, isInboundSms, isTest }) => (
      <ChannelAvatar
        key={`channelAvatar-${id}`}
        clinicPetParentId={id}
        displayName={displayName}
        isInboundSms={isInboundSms}
        isTest={isTest}
      />
    ));
  }, [petParentsWithDisplayName]);

  const petParentDisplayNames = useMemo(() => {
    return channelPetParents
      .map(({ creationSource, firstName, lastName, phoneNumbers }) =>
        getClinicPetParentDisplayName({
          firstName,
          lastName,
          creationSource,
          phoneNumbers,
        }),
      )
      .join(', ');
  }, [channelPetParents]);

  const petDisplayNames = useMemo(() => {
    return conversation.pets.map(({ name }) => name).join(', ');
  }, [conversation.pets]);

  // Timestamp
  const conversationDate = useMemo(() => {
    const dateValue: string = sortByLastMessageEnabled ? conversation.lastMessage?.createdAt : conversation.updatedAt;
    return !!dateValue ? new Date(dateValue) : new Date();
  }, [conversation.lastMessage?.createdAt, conversation.updatedAt, sortByLastMessageEnabled]);

  const handleConversationClick = useCallback(async () => {
    if (isConversationSelected) {
      return;
    }

    history.push(`${replaceParam(RouteDefinitions.Conversations, ':channelId', conversation.id)}`);
    if (isCurrentClinic) {
      setCurrentClinicPetParentId(firstPetParentId);
    }
  }, [
    conversation.id,
    firstPetParentId,
    history,
    isConversationSelected,
    isCurrentClinic,
    setCurrentClinicPetParentId,
  ]);

  const conversationStatus = useMemo(
    () => (
      <Tooltip label={conversation.channelStatus?.name || ''}>
        <Box maxW="120px">
          <Tag
            label={conversation.channelStatus?.name || ''}
            leftElement={
              <Flex>
                <Swatch bgColor={conversation.channelStatus?.color} w={3} h={3} />
              </Flex>
            }
            size="sm"
            closeButtonDisplay="always"
            bgColor="background.alpha"
          />
        </Box>
      </Tooltip>
    ),
    [conversation.channelStatus?.color, conversation.channelStatus?.name],
  );

  return (
    <ConversationCard
      allowHoverActions={false}
      onClick={handleConversationClick}
      isActive={isConversationSelected}
      showNotificationIndicator={hasUnreadMessages}
      shouldPreservePosition={isLoadingNextPage}
      avatarGroupProps={{
        avatars: channelAvatars,
        max: 1,
        onAvatarGroupClick: (): void => {
          if (conversationClinicId === currentClinicId) {
            viewClinicPetParent({ clinicPetParentId: firstPetParentId || '' });
          }
          handleAvatarClick();
        },
      }}
      headerProps={{
        title: petParentDisplayNames,
        subtitle: petDisplayNames,
        timestampProps: {
          date: conversationDate,
        },
      }}
      {...(conversation.lastMessage && {
        content: <CardMessage lastChannelMessage={conversation.lastMessage} channelId={conversation.id} />,
      })}
      contentMenu={conversationStatus}
      footerProps={{
        content: conversation.clinic?.name,
        iconProps: {
          name: 'clinic',
        },
      }}
    />
  );
};
